import { hasOnly, isId, isIntegerIn, isText, listOf, tileOf, type Fields } from "./op-read.ts";
import { OP_CELL_END_MAX, PATCH_CELL_RUNS_MAX, PATCH_GRIDS, PATCH_PALETTE_MAX, PATCH_VALUE_RUNS_MAX, type CellPatchIntent, type CellRun, type PatchGrid, type PatchValue, type ValueRun } from "./op-types.ts";

/**
 * The reader of a cell patch (ADR-086 decisions 19 and 20): its palette by the grid's kind, its cell runs
 * ascending and never overlapping, and its value runs covering exactly its cells, each within its limit.
 */

/**
 * Reads one value of a patch's palette.
 *
 * @param value The value.
 * @param grid The grid the patch writes.
 * @returns The value, or undefined when it is not one the grid holds: a byte for corners and collision, a
 *   type id or null for zones, a tile with its flips or null for tiles, a door's id or null for doors.
 */
function patchValueOf(value: unknown, grid: PatchGrid): PatchValue | undefined {
  switch (grid) {
    case "corners":
    case "collision":
      return isIntegerIn(value, 0, 255) ? value : undefined;
    case "zone":
      return value === null || isText(value, 1) ? value : undefined;
    case "tiles":
      return value === null ? null : tileOf(value, true);
    case "door":
      return value === null || isId(value) ? value : undefined;
  }
}

/**
 * Reads a patch's runs: pairs of integers.
 *
 * @param value The value.
 * @param most The most runs allowed.
 * @returns The runs, or undefined when the value is not a list of 1 to `most` pairs of integers.
 */
function runsOf(value: unknown, most: number): (readonly [number, number])[] | undefined {
  if (!Array.isArray(value) || value.length === 0 || value.length > most) {
    return undefined;
  }
  const runs: (readonly [number, number])[] = [];
  for (const run of value as readonly unknown[]) {
    if (!Array.isArray(run) || run.length !== 2 || !Number.isInteger(run[0]) || !Number.isInteger(run[1])) {
      return undefined;
    }
    runs.push([run[0] as number, run[1] as number]);
  }
  return runs;
}

/**
 * Reads a cell patch.
 *
 * @param fields The intent's fields.
 * @returns The patch, or undefined when a field is missing, extra or mistyped; the palette holds no value,
 *   more than `PATCH_PALETTE_MAX` or one its grid does not hold; a cell run is empty, starts before the one
 *   before it ends or reaches past `OP_CELL_END_MAX`; a value run is empty or names no palette value; or the
 *   value runs do not cover exactly the cells.
 */
export function patchOf(fields: Fields): CellPatchIntent | undefined {
  const { map, layer, grid, palette } = fields;
  if (!hasOnly(fields, ["k", "map", "layer", "grid", "palette", "cells", "values"]) || !isId(map) || !isId(layer) || typeof grid !== "string" || !PATCH_GRIDS.includes(grid as PatchGrid)) {
    return undefined;
  }
  if (!Array.isArray(palette) || palette.length === 0 || palette.length > PATCH_PALETTE_MAX) {
    return undefined;
  }
  const values = listOf(palette, (value) => {
    const read = patchValueOf(value, grid as PatchGrid);
    return read === undefined ? undefined : { read };
  });
  const cells = runsOf(fields["cells"], PATCH_CELL_RUNS_MAX);
  const runs = runsOf(fields["values"], PATCH_VALUE_RUNS_MAX);
  if (values === undefined || cells === undefined || runs === undefined) {
    return undefined;
  }
  let end = 0;
  let count = 0;
  for (const [start, length] of cells) {
    if (start < end || length < 1 || start + length > OP_CELL_END_MAX) {
      return undefined;
    }
    end = start + length;
    count += length;
  }
  let covered = 0;
  for (const [index, length] of runs) {
    if (index < 0 || index >= values.length || length < 1) {
      return undefined;
    }
    covered += length;
  }
  if (covered !== count) {
    return undefined;
  }
  return { k: "cells", map, layer, grid: grid as PatchGrid, palette: values.map((value) => value.read), cells: cells as CellRun[], values: runs as ValueRun[] };
}
